import { useTranslation } from "react-i18next";

import { useGetFlatHomeInfo } from "@/services/api";
import {
  SimCardDetail,
  SimCardDetailManageAllowedReasonsItem,
} from "@/services/model";
import { getLocalizedDateString } from "@/utils/dateHelpers";
import { hasWatchSimOrdered, isLockRequested } from "@/utils/deviceUtils";
import { isFlatHomeTariff } from "@/utils/tariffUtils";

interface SubscriptionActions {
  /** Reason the subscription options cannot be changed, if any. */
  optionsDisabledHint?: string;
  /** Reason the renewal cannot be paused or resumed, if any. */
  statusDisabledHint?: string;
}

/**
 * Works out whether "Change" and "Pause subscription" are available and, when they
 * are not, which single-sentence tooltip explains it.
 *
 * There is deliberately no "hide the buttons" flag: an unavailable action is
 * always rendered as a greyed-out button whose tooltip states the reason, so a
 * customer is never left wondering where an action went. A missing hint means
 * the action is available.
 */
export const useSubscriptionActions = (
  simDetail: SimCardDetail,
): SubscriptionActions => {
  const { t, i18n } = useTranslation("portal", {
    keyPrefix: "cockpit.managementTile.subscription",
  });

  const isFlatHome = isFlatHomeTariff(simDetail.serviceID);
  const { data: flatHomeInfo } = useGetFlatHomeInfo(simDetail.simId ?? 0, {
    query: { enabled: simDetail.simId !== undefined && isFlatHome },
  });

  const hasLockRequested = isLockRequested(simDetail);
  // A Watch SIM that is only ORDERED is already billed, but not yet paired —
  // pausing the subscription has to wait until it is ACTIVE. Distinct from a pending
  // SIM order, which still uses the generic "once the order is complete" copy.
  const hasOrderedWatchSim = hasWatchSimOrdered(simDetail) && !hasLockRequested;
  const isOrderInProgress = !!simDetail.servicePending && !hasLockRequested;
  const hasBlockingEsimAction = simDetail.manageAllowedReasons?.some((reason) =>
    [
      SimCardDetailManageAllowedReasonsItem.OPEN_ESIM_CHANGE,
      SimCardDetailManageAllowedReasonsItem.OPEN_ESIM_RESET,
      SimCardDetailManageAllowedReasonsItem.IS_FIXED_SERVICE_INCOMPLETE,
    ].includes(reason),
  );
  // The backend's authoritative "no management at all" flag. Both actions must
  // then explain themselves via a tooltip rather than silently disappear.
  const isManageForbidden = simDetail.manageAboAllowed === false;
  // Only once the answer is in: while the request is in flight the date for the
  // tooltip is still missing, and "Ab dem  kannst du ..." reads broken.
  const isWithinFlatHomeMinimumTerm =
    isFlatHome && flatHomeInfo !== undefined && !flatHomeInfo.manageable;

  const flatHomeHint = t("status.changeDisabledFlatHome", {
    date: flatHomeInfo?.manageableAt
      ? getLocalizedDateString(flatHomeInfo.manageableAt, i18n.language)
      : "",
  });

  return {
    optionsDisabledHint: hasLockRequested
      ? t("options.changeDisabledMissingPaymentMean")
      : hasOrderedWatchSim
        ? t("options.changeDisabledWatchSim")
        : isOrderInProgress || isManageForbidden
          ? t("options.changeDisabled")
          : undefined,
    statusDisabledHint: isWithinFlatHomeMinimumTerm
      ? flatHomeHint
      : hasOrderedWatchSim
        ? t("status.changeDisabledWatchSim")
        : isOrderInProgress || hasBlockingEsimAction || isManageForbidden
          ? t("status.changeDisabled")
          : undefined,
  };
};
